import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { toast } from "sonner";
import { AlertOctagon, ShieldCheck, CheckCircle2 } from "lucide-react";

const CommandCenterConsentPage = () => {
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);
  const [fullName, setFullName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(null);

  useEffect(() => {
    api.get("/command-center/me").then((r) => {
      if (r.data.data_transfer_consent_signed_at) setSigned(r.data.data_transfer_consent_signed_at);
    }).catch(() => {});
  }, []);

  const submit = async () => {
    if (!acknowledged) { toast.error("Please acknowledge the consent"); return; }
    if (!fullName.trim() || fullName.trim().length < 3) { toast.error("Please type your full legal name"); return; }
    setSubmitting(true);
    try {
      await api.post("/command-center/consent/sign", {
        full_name_typed: fullName.trim(),
        consent_acknowledged: acknowledged,
      });
      toast.success("Consent signed");
      navigate("/command-center");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sign");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="Data Transfer Consent" subtitle="Required before regulated-partner handoff">
      <div className="mb-6 p-4 rounded-lg border-2 border-red-300 bg-red-50 flex gap-3 items-start">
        <AlertOctagon className="text-red-700 shrink-0 mt-0.5" size={20} />
        <p className="text-red-900 text-sm leading-relaxed">
          Do not send funds to EchoSense, any introducer, any individual, or any account based on verbal instructions.
          Only follow written funding instructions issued by the approved regulated partner or fund administrator
          after your file has been approved.
        </p>
      </div>

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <ShieldCheck size={20} className="text-blue-700" /> Data Transfer Consent
          </CardTitle>
          <CardDescription className="text-slate-600">
            EchoSense requires your consent before transferring your verified file to an approved regulated partner.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {signed ? (
            <div className="p-4 rounded border border-emerald-300 bg-emerald-50 flex items-center gap-3">
              <CheckCircle2 className="text-emerald-700" size={24} />
              <div>
                <p className="text-emerald-900 font-semibold">Consent signed</p>
                <p className="text-sm text-emerald-900/80">Signed at {signed}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="prose prose-sm max-w-none text-slate-800 leading-relaxed">
                <p>
                  By signing below, I authorize EchoSense Capital Markets to share my onboarding file — including my
                  identity verification (KYC), accredited / qualified investor verification, and the answers I provided
                  in my onboarding intake — with the EchoSense-approved regulated partner for the limited purpose of
                  final compliance review, account documentation, and (if approved by the partner) the issuance of
                  written funding instructions.
                </p>
                <p>
                  I understand and acknowledge that:
                </p>
                <ul>
                  <li>EchoSense does not provide investment advice or discuss returns.</li>
                  <li>EchoSense does not receive client funds or issue funding instructions.</li>
                  <li>Final account approval is made by the regulated partner, not EchoSense.</li>
                  <li>I will only follow written instructions from the approved regulated partner.</li>
                  <li>I am free to withdraw this consent at any time prior to the transfer.</li>
                </ul>
              </div>

              <div className="mt-6 space-y-3">
                <label className="flex items-start gap-3 p-3 rounded border border-slate-200 bg-slate-50 cursor-pointer">
                  <Checkbox
                    data-testid="consent-ack"
                    checked={acknowledged}
                    onCheckedChange={setAcknowledged}
                    className="mt-1"
                  />
                  <span className="text-sm text-slate-900">
                    I have read and understand the above. I authorize EchoSense to share my onboarding file with the
                    approved regulated partner.
                  </span>
                </label>
                <div>
                  <Label className="text-slate-900">Type your full legal name to sign</Label>
                  <Input
                    data-testid="consent-name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Full legal name"
                    className="mt-1 bg-white text-slate-900 border-slate-300"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => navigate("/command-center")}>Cancel</Button>
                  <Button
                    data-testid="consent-submit"
                    onClick={submit}
                    disabled={submitting || !acknowledged}
                    className="bg-blue-700 hover:bg-blue-800 text-white"
                  >
                    {submitting ? "Signing..." : "Sign & Submit"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default CommandCenterConsentPage;
